import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'

import { TDeliveryType } from '@/shared/types/deprecated-types'

import { Gift } from '../types/gifts'
import { giftConfigLsService } from '../utils/gift-config-ls-service'

import {
	addedProductToCartEvent,
	removedGiftForProductEvent,
	removedProductFromCartEvent,
	selectedGiftForProductEvent
} from './events'

// Новая изолированная структура для подарков
export type ProductGiftConfig = {
	type: 'one' | 'packages' | 'auto'
	// Выбранный подарок (для type='one') или null
	selectedGift: Gift | null
	// Доступные подарки на выбор (для type='one')
	availableOneGifts: Gift[]
	// Доступные пакеты (для type='packages')
	availablePackages: Gift[][]
	// Индекс выбранного пакета (для type='packages')
	selectedPackageIndex: number | null
	// Авто-подарки исключены?
	autoGiftsExcluded: boolean
}

export type TCartState = {
	delivery: TDeliveryType | null
	// Gift system
	gifts: EntityState<Gift>
	// НОВАЯ АРХИТЕКТУРА: изолированная конфигурация подарков для каждого товара
	productGiftsConfig: Record<number, ProductGiftConfig>
	// СТАРАЯ АРХИТЕКТУРА (deprecated, будет удалена):
	// auto gifts (selected package contents per product)
	allGiftsByProduct: Record<number, Gift[]>
	// packages support
	allGiftPackagesByProduct: Record<number, Gift[][]>
	selectedAllPackageIndexByProduct: Record<number, number | undefined>
	// visibility/exclusion flags
	excludedAutoGiftsByProduct: Record<number, boolean>
	excludedAutoGiftIdsByProduct: Record<number, number[]>
}

export const cartGiftsAdapter = createEntityAdapter<Gift>({
	selectId: gift => gift.PRODUCT_IDS[0],
	sortComparer: (a, b) => a.ID - b.ID
})

const initialState: TCartState = {
	// Order-related fields
	delivery: null,
	// Gift system
	gifts: cartGiftsAdapter.getInitialState(),
	productGiftsConfig: {},
	allGiftsByProduct: {},
	allGiftPackagesByProduct: {},
	selectedAllPackageIndexByProduct: {},
	excludedAutoGiftsByProduct: {},
	excludedAutoGiftIdsByProduct: {}
}

const cart = createSlice({
	name: `cart`,
	initialState,
	reducers: {
		clearBasketSuccess: (state: TCartState) => {
			cartGiftsAdapter.removeAll(state.gifts)
		},
		// gifts sync on init (restores selected one-gifts from LS)
		syncGiftsOnInit: (
			state,
			action: PayloadAction<{
				gifts: Gift[]
				productIdsInCart: number[]
				lastSyncedGifts: Record<number, number>
			}>
		) => {
			const { gifts, productIdsInCart, lastSyncedGifts } = action.payload
			const selectedGifts: Record<number, Gift> = {}
			for (const productId of productIdsInCart) {
				const productAvailableGifts = gifts.filter(gift => gift.PRODUCT_IDS.includes(productId))
				const productEarlierSelectedGift = productAvailableGifts.find(gift => gift.ID === lastSyncedGifts[productId])
				if (productEarlierSelectedGift) {
					selectedGifts[productId] = productEarlierSelectedGift
				}
			}
			cartGiftsAdapter.setAll(state.gifts, selectedGifts)
		},

		// AUTO gifts (legacy flat list per product kept for compatibility)
		setAllGiftsForProduct: (state, action: PayloadAction<{ productId: number; gifts: Gift[] }>) => {
			const { productId, gifts } = action.payload
			const normalized = gifts.map(g => ({ ...g, PRODUCT_IDS: [productId] }))
			const unique = Array.from(new Map(normalized.map(g => [g.ID, g])).values())
			const prev = state.allGiftsByProduct[productId] || []
			const prevIds = prev.map(g => g.ID).join(',')
			const nextIds = unique.map(g => g.ID).join(',')
			if (prevIds !== nextIds) {
				state.allGiftsByProduct[productId] = unique
			}
		},
		clearAllGiftsForProduct: (state, action: PayloadAction<{ productId: number }>) => {
			const { productId } = action.payload
			delete state.allGiftsByProduct[productId]
		},

		// PACKAGES support
		setAllGiftPackagesForProduct: (state, action: PayloadAction<{ productId: number; packages: Gift[][] }>) => {
			const { productId, packages } = action.payload
			const normalizedPackages = packages.map(pkg =>
				Array.from(new Map(pkg.map(g => [g.ID, { ...g, PRODUCT_IDS: [productId] }])).values())
			)
			state.allGiftPackagesByProduct[productId] = normalizedPackages
		},
		selectAllGiftPackageIndex: (state, action: PayloadAction<{ productId: number; index: number }>) => {
			const { productId, index } = action.payload
			state.selectedAllPackageIndexByProduct[productId] = index
			const pkg = state.allGiftPackagesByProduct[productId]?.[index] || []
			state.allGiftsByProduct[productId] = pkg
		},
		clearAllGiftPackagesForProduct: (state, action: PayloadAction<{ productId: number }>) => {
			const { productId } = action.payload
			delete state.allGiftPackagesByProduct[productId]
			delete state.selectedAllPackageIndexByProduct[productId]
		},

		// visibility/exclusions
		setAutoGiftsExcludedForProduct: (state, action: PayloadAction<{ productId: number; excluded: boolean }>) => {
			const { productId, excluded } = action.payload
			state.excludedAutoGiftsByProduct[productId] = excluded
		},
		excludeAutoGiftForProduct: (state, action: PayloadAction<{ productId: number; giftId: number }>) => {
			const { productId, giftId } = action.payload
			const list = state.excludedAutoGiftIdsByProduct[productId] || []
			if (!list.includes(giftId)) state.excludedAutoGiftIdsByProduct[productId] = [...list, giftId]
		},
		includeAutoGiftForProduct: (state, action: PayloadAction<{ productId: number; giftId: number }>) => {
			const { productId, giftId } = action.payload
			const list = state.excludedAutoGiftIdsByProduct[productId] || []
			state.excludedAutoGiftIdsByProduct[productId] = list.filter(id => id !== giftId)
		},
		clearExcludedAutoGiftsForProduct: (state, action: PayloadAction<{ productId: number }>) => {
			const { productId } = action.payload
			delete state.excludedAutoGiftIdsByProduct[productId]
		},

		// ============================================================
		// НОВАЯ АРХИТЕКТУРА: изолированная конфигурация подарков
		// ============================================================
		setProductGiftConfig: (state, action: PayloadAction<{ productId: number; config: ProductGiftConfig }>) => {
			const { productId, config } = action.payload
			console.log(`[REDUCER setProductGiftConfig] productId=${productId}`, config)
			state.productGiftsConfig[productId] = config
			// Сохраняем в localStorage для персистентности
			giftConfigLsService.set(productId, config)
			// ВАЖНО: синхронизируем со старым полем для обратной совместимости
			if (config.autoGiftsExcluded) {
				state.excludedAutoGiftsByProduct[productId] = true
			}
		},
		updateProductGiftConfig: (
			state,
			action: PayloadAction<{ productId: number; updates: Partial<ProductGiftConfig> }>
		) => {
			const { productId, updates } = action.payload
			const existing = state.productGiftsConfig[productId]
			if (existing) {
				const updated = { ...existing, ...updates }
				state.productGiftsConfig[productId] = updated
				// Сохраняем в localStorage
				giftConfigLsService.set(productId, updated)
			}
		},
		selectOneGiftForProduct: (state, action: PayloadAction<{ productId: number; gift: Gift | null }>) => {
			const { productId, gift } = action.payload
			const config = state.productGiftsConfig[productId]
			if (config && config.type === 'one') {
				config.selectedGift = gift
				// Сохраняем в localStorage
				giftConfigLsService.set(productId, config)
			}
		},
		selectPackageForProduct: (state, action: PayloadAction<{ productId: number; packageIndex: number }>) => {
			const { productId, packageIndex } = action.payload
			const config = state.productGiftsConfig[productId]
			if (config && config.type === 'packages') {
				config.selectedPackageIndex = packageIndex
				// Сохраняем в localStorage
				giftConfigLsService.set(productId, config)
			}
		},
		toggleAutoGiftsForProduct: (state, action: PayloadAction<{ productId: number; excluded: boolean }>) => {
			const { productId, excluded } = action.payload
			const config = state.productGiftsConfig[productId]
			if (config && (config.type === 'auto' || config.type === 'packages')) {
				config.autoGiftsExcluded = excluded
				// Сохраняем в localStorage
				giftConfigLsService.set(productId, config)
			}
			// ВАЖНО: также обновляем старое поле для обратной совместимости (используется в computeCheckoutGifts)
			state.excludedAutoGiftsByProduct[productId] = excluded
		},
		clearProductGiftConfig: (state, action: PayloadAction<{ productId: number }>) => {
			const { productId } = action.payload
			delete state.productGiftsConfig[productId]
			// Удаляем из localStorage
			giftConfigLsService.remove(productId)
		}
	},

	extraReducers: builder => {
		builder.addCase(addedProductToCartEvent, (state, action) => {
			const { gift, productId } = action.payload
			if (gift) {
				const normalized: Gift = { ...gift, PRODUCT_IDS: [productId] }
				cartGiftsAdapter.addOne(state.gifts, normalized)
			}
		})
		builder.addCase(removedProductFromCartEvent, (state, action) => {
			const { productId } = action.payload
			cartGiftsAdapter.removeOne(state.gifts, productId)
			delete state.productGiftsConfig[productId]
			delete state.allGiftsByProduct[productId]
			delete state.allGiftPackagesByProduct[productId]
			delete state.selectedAllPackageIndexByProduct[productId]
			delete state.excludedAutoGiftsByProduct[productId]
			delete state.excludedAutoGiftIdsByProduct[productId]
		})
		builder.addCase(selectedGiftForProductEvent, (state, action) => {
			const { gift } = action.payload
			const keyProductId = gift.PRODUCT_IDS[0]
			const selectedAlready = cartGiftsAdapter.getSelectors().selectById(state.gifts, keyProductId)
			if (selectedAlready) cartGiftsAdapter.removeOne(state.gifts, keyProductId)
			const normalized: Gift = { ...gift, PRODUCT_IDS: [keyProductId] }
			cartGiftsAdapter.addOne(state.gifts, normalized)
		})
		builder.addCase(removedGiftForProductEvent, (state, action) => {
			const { gift } = action.payload

			cartGiftsAdapter.removeOne(state.gifts, gift.PRODUCT_IDS[0])
		})
	}
})

export const {
	clearBasketSuccess,
	setAllGiftsForProduct,
	clearAllGiftsForProduct,
	setAllGiftPackagesForProduct,
	selectAllGiftPackageIndex,
	clearAllGiftPackagesForProduct,
	setAutoGiftsExcludedForProduct,
	excludeAutoGiftForProduct,
	includeAutoGiftForProduct,
	clearExcludedAutoGiftsForProduct,
	setProductGiftConfig,
	updateProductGiftConfig,
	selectOneGiftForProduct,
	selectPackageForProduct,
	toggleAutoGiftsForProduct,
	clearProductGiftConfig
} = cart.actions

export const { reducer: cartReducer } = cart
export const { actions: cartActions } = cart
